/**
 * Niveau courant de la coquille (lot U6b, vague 1 ; § 4.3) : Plateforme, Organisation,
 * Programme ou Projet.
 *
 * La ROUTE décide quand elle appartient à un niveau (`/plateforme/*`, `/programme/*`, écrans
 * d'organisation, écrans du projet). Une route transverse (Accueil, Boîte d'attention,
 * Notifications, `/items/…` hors navigation…) GARDE le dernier niveau connu : ouvrir l'accueil
 * depuis le niveau Programme ne fait pas sauter la barre latérale au niveau Projet. Sans mémoire,
 * le niveau est Projet si un espace est ouvert, Organisation sinon. La Plateforme n'est jamais
 * rendue à qui n'est pas super-administrateur.
 */
import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { niveauDuChemin, type Niveau } from '@/lib/nav'

const CLE = 'doit-niveau-courant'
const NIVEAUX: readonly Niveau[] = ['plateforme', 'organisation', 'programme', 'projet']

function lire(): Niveau | null {
  try {
    const v = sessionStorage.getItem(CLE)
    return v && (NIVEAUX as readonly string[]).includes(v) ? (v as Niveau) : null
  } catch {
    return null
  }
}

function ecrire(n: Niveau): void {
  try { sessionStorage.setItem(CLE, n) } catch { /* stockage indisponible : mémoire du rendu seulement */ }
}

/** Règle pure, testée à part : route d'abord, puis mémoire, puis espace ouvert. */
export function resoudreNiveau({ duChemin, dernier, espaceOuvert, superAdmin }: {
  duChemin: Niveau | undefined
  dernier: Niveau | null
  espaceOuvert: boolean
  superAdmin: boolean
}): Niveau {
  const n = duChemin ?? dernier ?? (espaceOuvert ? 'projet' : 'organisation')
  if (n === 'plateforme' && !superAdmin) return espaceOuvert ? 'projet' : 'organisation'
  return n
}

export function useNiveau({ espaceOuvert, superAdmin }: { espaceOuvert: boolean; superAdmin: boolean }): Niveau {
  const { pathname, search } = useLocation()
  const duChemin = niveauDuChemin(pathname, search)
  const [dernier, setDernier] = useState<Niveau | null>(() => lire())
  useEffect(() => {
    if (duChemin && duChemin !== dernier) {
      setDernier(duChemin)
      ecrire(duChemin)
    }
  }, [duChemin, dernier])
  return resoudreNiveau({ duChemin, dernier, espaceOuvert, superAdmin })
}
